import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from '../../util/Toast';

// Helper function to extract a cookie value by name
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const AdminFirePostbackPage = () => {
  const navigate = useNavigate();

  // ========== STATE ==========
  const [offers, setOffers] = useState([]);
  const [offersLoading, setOffersLoading] = useState(true);

  const [selectedOffer, setSelectedOffer] = useState(null);
  const [selectedEvents, setSelectedEvents] = useState([]);

  const [tids, setTids] = useState([]);
  const [tidInput, setTidInput] = useState('');

  const [successPattern, setSuccessPattern] = useState('');

  const [isOfferDropdownOpen, setIsOfferDropdownOpen] = useState(false);
  const [isEventDropdownOpen, setIsEventDropdownOpen] = useState(false);

  const [parallelMode, setParallelMode] = useState(true);
  const [isFiring, setIsFiring] = useState(false);
  const [results, setResults] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const fileInputRef = useRef(null);
  const offerDropdownRef = useRef(null);
  const eventDropdownRef = useRef(null);

  // ========== FETCH OFFERS ==========
  useEffect(() => {
    const fetchOffers = async () => {
      try {
        setOffersLoading(true);
        const baseUrl = process.env.REACT_APP_API_URL || '';
        const token = getCookie('adminToken') || getCookie('authToken');

        const response = await axios.get(`${baseUrl}/api/admin/fire-postback/offers`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        const list = response.data?.data || response.data || [];
        setOffers(Array.isArray(list) ? list : []);
      } catch (error) {
        console.error('Error fetching offers:', error);
        if (error.response?.status === 401) {
          navigate('/emAdmin7/login');
          return;
        }
        toast.error(error.response?.data?.msg || 'Failed to load offers');
      } finally {
        setOffersLoading(false);
      }
    };

    fetchOffers();
  }, [navigate]);

  // ========== CLOSE DROPDOWNS ON OUTSIDE CLICK ==========
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (offerDropdownRef.current && !offerDropdownRef.current.contains(e.target)) {
        setIsOfferDropdownOpen(false);
      }
      if (eventDropdownRef.current && !eventDropdownRef.current.contains(e.target)) {
        setIsEventDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ========== DERIVED EVENTS FOR SELECTED OFFER ==========
  const availableEvents = useMemo(() => {
    if (!selectedOffer) return [];
    let parsed = [];
    try {
      parsed = typeof selectedOffer.event_config === 'string'
        ? JSON.parse(selectedOffer.event_config || '[]')
        : (selectedOffer.event_config || selectedOffer.events || []);
    } catch (err) {
      console.log('Event parse error:', err);
    }
    return Array.isArray(parsed)
      ? parsed.map((ev) => ({
          id: String(ev.event_id || ev.id),
          name: ev.event_name || ev.title || ev.name || 'Event'
        }))
      : [];
  }, [selectedOffer]);

  // Reset events when offer changes
  useEffect(() => {
    setSelectedEvents([]);
  }, [selectedOffer]);

  // ========== TID TAG HANDLING ==========
  const addTidValue = (raw) => {
    const parts = String(raw)
      .split(/[\s,]+/)
      .map((t) => t.trim())
      .filter(Boolean);
    if (!parts.length) return;
    setTids((prev) => {
      const merged = [...prev];
      parts.forEach((p) => {
        if (!merged.includes(p)) merged.push(p);
      });
      return merged;
    });
  };

  const handleTidKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === ',' || e.key === 'Tab') {
      e.preventDefault();
      if (tidInput.trim()) {
        addTidValue(tidInput);
        setTidInput('');
      }
    } else if (e.key === 'Backspace' && !tidInput && tids.length) {
      setTids((prev) => prev.slice(0, -1));
    }
  };

  const handleTidPaste = (e) => {
    const pasted = e.clipboardData.getData('text');
    if (!pasted) return;
    e.preventDefault();
    addTidValue(pasted);
  };

  const removeTid = (tid) => {
    setTids((prev) => prev.filter((t) => t !== tid));
  };

  // ========== FILE IMPORT ==========
  const handleFileImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target.result || '';
      addTidValue(content);
      toast.success(`Imported TIDs from ${file.name}`);
    };
    reader.onerror = () => toast.error('Failed to read file');
    reader.readAsText(file);
    e.target.value = '';
  };

  // ========== OFFER / EVENT SELECTION ==========
  const toggleEvent = (evId) => {
    setSelectedEvents((prev) =>
      prev.includes(evId) ? prev.filter((id) => id !== evId) : [...prev, evId]
    );
  };

  // ========== MATCH SUCCESS PATTERN ==========
  const matchesSuccessPattern = (responseText) => {
    if (!successPattern.trim()) return true; // If no pattern, rely on HTTP success
    try {
      return String(responseText || '')
        .toLowerCase()
        .includes(successPattern.trim().toLowerCase());
    } catch {
      return false;
    }
  };

  // ========== FIRE POSTBACK (shared logic) ==========
  const runPostbacks = async (pairs, startIndex = 0, existingResults = null) => {
    const baseUrl = process.env.REACT_APP_API_URL || '';
    const token = getCookie('adminToken') || getCookie('authToken');

    if (existingResults) {
      setResults(existingResults);
    } else {
      const initialRows = pairs.map((p) => ({
        tid: p.tid,
        eventId: p.eventId,
        status: 'pending',
        response: ''
      }));
      setResults(initialRows);
    }

    setIsFiring(true);

    const updateRow = (index, patch) => {
      setResults((prev) => {
        const copy = [...prev];
        copy[index] = { ...copy[index], ...patch };
        return copy;
      });
    };

    const fireOne = async (pair, index) => {
      try {
        const res = await axios.post(
          `${baseUrl}/api/admin/fire-postback`,
          {
            offerId: selectedOffer.offer_id || selectedOffer.id,
            eventId: pair.eventId,
            tid: pair.tid
          },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );

        const responseText =
          res.data?.msg ||
          res.data?.data?.response ||
          res.data?.response ||
          'Fired successfully';

        const httpOk = res.status >= 200 && res.status < 300;
        const patternOk = matchesSuccessPattern(responseText);

        updateRow(index, {
          status: httpOk && patternOk ? 'success' : 'error',
          response: responseText
        });
      } catch (err) {
        if (err.response?.status === 401) {
          navigate('/emAdmin7/login');
          return;
        }
        const responseText =
          err.response?.data?.msg ||
          err.response?.data?.data?.response ||
          err.message ||
          'Failed';
        updateRow(index, {
          status: 'error',
          response: responseText
        });
      }
    };

    try {
      if (parallelMode) {
        await Promise.all(pairs.map((p, i) => fireOne(p, startIndex + i)));
      } else {
        for (let i = 0; i < pairs.length; i++) {
          await fireOne(pairs[i], startIndex + i);
        }
      }
      toast.success('Postback firing completed');
    } catch (err) {
      console.error('Firing error:', err);
      toast.error('Something went wrong while firing postbacks');
    } finally {
      setIsFiring(false);
    }
  };

  // ========== FIRE POSTBACK ==========
  const firePostback = async () => {
    if (!selectedOffer) {
      toast.error('Please select an offer');
      return;
    }
    if (!selectedEvents.length) {
      toast.error('Please select at least one event');
      return;
    }
    if (!tids.length) {
      toast.error('Please add at least one TID');
      return;
    }

    const pairs = [];
    selectedEvents.forEach((evId) => {
      tids.forEach((tid) => {
        pairs.push({ tid, eventId: evId });
      });
    });

    setCurrentPage(1);
    await runPostbacks(pairs);
  };

  // ========== RETRY ALL FAILED ==========
  const retryAllFailed = async () => {
    if (!selectedOffer) {
      toast.error('Please select an offer first');
      return;
    }

    const failedIndexes = results
      .map((r, i) => (r.status === 'error' ? i : -1))
      .filter((i) => i !== -1);

    if (!failedIndexes.length) {
      toast.error('No failed postbacks to retry');
      return;
    }

    // Reset those rows to pending
    setResults((prev) => {
      const copy = [...prev];
      failedIndexes.forEach((i) => {
        copy[i] = { ...copy[i], status: 'pending', response: '' };
      });
      return copy;
    });

    const baseUrl = process.env.REACT_APP_API_URL || '';
    const token = getCookie('adminToken') || getCookie('authToken');

    setIsFiring(true);

    const fireOne = async (row, index) => {
      try {
        const res = await axios.post(
          `${baseUrl}/api/admin/fire-postback`,
          {
            offerId: selectedOffer.offer_id || selectedOffer.id,
            eventId: row.eventId,
            tid: row.tid
          },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );

        const responseText =
          res.data?.msg ||
          res.data?.data?.response ||
          res.data?.response ||
          'Fired successfully';

        const httpOk = res.status >= 200 && res.status < 300;
        const patternOk = matchesSuccessPattern(responseText);

        setResults((prev) => {
          const copy = [...prev];
          copy[index] = {
            ...copy[index],
            status: httpOk && patternOk ? 'success' : 'error',
            response: responseText
          };
          return copy;
        });
      } catch (err) {
        if (err.response?.status === 401) {
          navigate('/emAdmin7/login');
          return;
        }
        const responseText =
          err.response?.data?.msg ||
          err.response?.data?.data?.response ||
          err.message ||
          'Failed';
        setResults((prev) => {
          const copy = [...prev];
          copy[index] = {
            ...copy[index],
            status: 'error',
            response: responseText
          };
          return copy;
        });
      }
    };

    try {
      if (parallelMode) {
        await Promise.all(failedIndexes.map((i, k) => fireOne(results[i], i)));
      } else {
        for (const i of failedIndexes) {
          await fireOne(results[i], i);
        }
      }
      toast.success('Retry completed');
    } catch (err) {
      console.error('Retry error:', err);
      toast.error('Something went wrong while retrying');
    } finally {
      setIsFiring(false);
    }
  };

  // ========== EXPORT FAILED TIDs AS CSV ==========
  const exportFailedTids = () => {
    const failedTids = Array.from(
      new Set(results.filter((r) => r.status === 'error').map((r) => r.tid))
    );

    if (!failedTids.length) {
      toast.error('No failed TIDs to export');
      return;
    }

    const csvContent = ['tid', ...failedTids].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `failed-tids-${Date.now()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success(`Exported ${failedTids.length} failed TID(s)`);
  };

  // ========== STATS ==========
  const successCount = results.filter((r) => r.status === 'success').length;
  const errorCount = results.filter((r) => r.status === 'error').length;
  const pendingCount = results.filter((r) => r.status === 'pending').length;

  // ========== PAGINATION ==========
  const totalPages = Math.max(1, Math.ceil(results.length / rowsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedResults = results.slice(
    (safePage - 1) * rowsPerPage,
    safePage * rowsPerPage
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const getOfferLabel = (o) => o.name || o.offer_name || `Offer #${o.offer_id || o.id}`;
  const getOfferId = (o) => o.offer_id || o.id;

  return (
    <div className="min-h-screen bg-zinc-950 font-sans text-zinc-100 w-full pt-4 lg:pt-20 lg:pl-72 transition-all duration-500">
      <style>{`
        @keyframes springIn {
          0% { opacity: 0; transform: translateY(20px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-spring-in {
          animation: springIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .shimmer-bg {
          background: linear-gradient(90deg, rgba(39,39,42,0.6) 25%, rgba(63,63,70,0.6) 50%, rgba(39,39,42,0.6) 75%);
          background-size: 200% 100%;
          animation: shimmer 1.8s infinite;
        }
        .micro-interaction {
          transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
        }
        .micro-interaction:hover { transform: translateY(-2px); }
        .micro-interaction:active { transform: translateY(0px) scale(0.98); }

        .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: rgba(255,255,255,0.05); border-radius: 8px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.2); border-radius: 8px; }
      `}</style>

      <div className="p-4 sm:p-8 space-y-8 max-w-[1600px] mx-auto mt-2">

        {/* ========== HEADER ========== */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 animate-spring-in">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-orange-500/10 text-orange-400 rounded-2xl border border-orange-500/20">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-black tracking-tight text-zinc-100">Fire Postback</h1>
              <p className="text-xs font-medium text-zinc-400 mt-0.5">
                Manually trigger conversions for specific TIDs across offers and events.
              </p>
            </div>
          </div>

          {results.length > 0 && (
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider">
              <span className="px-3 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                {successCount} Success
              </span>
              <span className="px-3 py-1.5 rounded-lg bg-rose-500/10 text-rose-400 border border-rose-500/20">
                {errorCount} Failed
              </span>
              {pendingCount > 0 && (
                <span className="px-3 py-1.5 rounded-lg bg-amber-500/10 text-amber-400 border border-amber-500/20">
                  {pendingCount} Pending
                </span>
              )}
            </div>
          )}
        </div>

        {/* ========== CONFIG CARD ========== */}
        <div className="bg-zinc-900/90 border border-zinc-800 rounded-3xl p-6 sm:p-8 shadow-lg space-y-6 animate-spring-in">

          {/* Offer + Event dropdown row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

            {/* ===== CUSTOM OFFER DROPDOWN ===== */}
            <div className="space-y-2" ref={offerDropdownRef}>
              <label className="text-[11px] font-black uppercase tracking-widest text-zinc-400">
                Select Offer
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsOfferDropdownOpen((v) => !v)}
                  className={`w-full flex items-center justify-between px-4 py-3.5 bg-zinc-950 border rounded-2xl text-sm font-bold text-left transition-all micro-interaction ${
                    isOfferDropdownOpen
                      ? 'border-emerald-500/50 ring-2 ring-emerald-500/10 shadow-lg shadow-emerald-500/10'
                      : 'border-zinc-800 hover:border-zinc-700'
                  }`}
                >
                  <span className={`truncate ${selectedOffer ? 'text-zinc-100' : 'text-zinc-500'}`}>
                    {offersLoading
                      ? 'Loading offers...'
                      : selectedOffer
                      ? getOfferLabel(selectedOffer)
                      : 'Choose an offer'}
                  </span>
                  <svg
                    className={`w-4 h-4 text-zinc-400 transition-transform duration-300 shrink-0 ${isOfferDropdownOpen ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {isOfferDropdownOpen && (
                  <div className="absolute z-30 mt-2 w-full max-h-72 overflow-y-auto custom-scrollbar bg-zinc-950 border border-zinc-800 rounded-2xl shadow-2xl animate-spring-in">
                    {offersLoading ? (
                      <div className="p-4 space-y-2">
                        {[...Array(3)].map((_, i) => (
                          <div key={i} className="h-10 rounded-xl shimmer-bg" />
                        ))}
                      </div>
                    ) : offers.length === 0 ? (
                      <div className="p-5 text-center text-xs text-zinc-500 font-medium italic">
                        No offers available
                      </div>
                    ) : (
                      offers.map((o) => {
                        const id = String(getOfferId(o));
                        const active = selectedOffer && String(getOfferId(selectedOffer)) === id;
                        return (
                          <button
                            key={id}
                            type="button"
                            onClick={() => {
                              setSelectedOffer(o);
                              setIsOfferDropdownOpen(false);
                            }}
                            className={`w-full flex items-center justify-between gap-3 px-4 py-3 text-left text-sm font-semibold transition-colors ${
                              active
                                ? 'bg-emerald-500/10 text-emerald-400'
                                : 'text-zinc-300 hover:bg-zinc-900'
                            }`}
                          >
                            <span className="truncate">{getOfferLabel(o)}</span>
                            <span className="text-[10px] font-mono text-zinc-500 shrink-0">
                              #{id}
                            </span>
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* ===== CUSTOM MULTI-SELECT EVENT DROPDOWN ===== */}
            <div className="space-y-2" ref={eventDropdownRef}>
              <label className="text-[11px] font-black uppercase tracking-widest text-zinc-400">
                Select Events
                <span className="ml-2 text-emerald-400 normal-case font-bold">
                  ({selectedEvents.length} selected)
                </span>
              </label>
              <div className="relative">
                <button
                  type="button"
                  disabled={!selectedOffer}
                  onClick={() => setIsEventDropdownOpen((v) => !v)}
                  className={`w-full flex items-center justify-between px-4 py-3.5 bg-zinc-950 border rounded-2xl text-sm font-bold text-left transition-all micro-interaction disabled:opacity-50 disabled:cursor-not-allowed ${
                    isEventDropdownOpen
                      ? 'border-emerald-500/50 ring-2 ring-emerald-500/10 shadow-lg shadow-emerald-500/10'
                      : 'border-zinc-800 hover:border-zinc-700'
                  }`}
                >
                  <span className={`truncate ${selectedEvents.length ? 'text-zinc-100' : 'text-zinc-500'}`}>
                    {!selectedOffer
                      ? 'Select an offer first'
                      : selectedEvents.length === 0
                      ? 'Choose events'
                      : selectedEvents
                          .map((id) => availableEvents.find((e) => e.id === id)?.name || id)
                          .join(', ')}
                  </span>
                  <svg
                    className={`w-4 h-4 text-zinc-400 transition-transform duration-300 shrink-0 ${isEventDropdownOpen ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {isEventDropdownOpen && selectedOffer && (
                  <div className="absolute z-30 mt-2 w-full max-h-72 overflow-y-auto custom-scrollbar bg-zinc-950 border border-zinc-800 rounded-2xl shadow-2xl animate-spring-in">
                    {availableEvents.length === 0 ? (
                      <div className="p-5 text-center text-xs text-zinc-500 font-medium italic">
                        No events configured for this offer
                      </div>
                    ) : (
                      availableEvents.map((ev) => {
                        const checked = selectedEvents.includes(ev.id);
                        return (
                          <button
                            key={ev.id}
                            type="button"
                            onClick={() => toggleEvent(ev.id)}
                            className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-semibold transition-colors ${
                              checked
                                ? 'bg-emerald-500/10 text-emerald-400'
                                : 'text-zinc-300 hover:bg-zinc-900'
                            }`}
                          >
                            <span
                              className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
                                checked
                                  ? 'bg-emerald-500 border-emerald-500 text-zinc-950'
                                  : 'border-zinc-600'
                              }`}
                            >
                              {checked && (
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </span>
                            <span className="truncate flex-1">{ev.name}</span>
                            <span className="text-[10px] font-mono text-zinc-500 shrink-0">#{ev.id}</span>
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ===== SUCCESS RESPONSE PATTERN ===== */}
          <div className="space-y-2">
            <label className="text-[11px] font-black uppercase tracking-widest text-zinc-400">
              Success Response Pattern
              <span className="ml-2 text-emerald-400 normal-case font-bold">
                (optional)
              </span>
            </label>
            <input
              type="text"
              value={successPattern}
              onChange={(e) => setSuccessPattern(e.target.value)}
              placeholder='e.g. "success", "OK", "conversion added"'
              className="w-full px-4 py-3.5 bg-zinc-950 border border-zinc-800 rounded-2xl text-sm font-mono text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/10 transition-all"
            />
            <p className="text-[10px] font-medium text-zinc-500">
              If the postback response body contains this text (case-insensitive), the row is marked
              <span className="text-emerald-400 font-bold"> Success</span>. Otherwise it's marked
              <span className="text-rose-400 font-bold"> Failed</span>. Leave empty to rely on HTTP status.
            </p>
          </div>

          {/* ===== TID TAG INPUT ===== */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-[11px] font-black uppercase tracking-widest text-zinc-400">
                Transaction IDs
                <span className="ml-2 text-emerald-400 normal-case font-bold">
                  ({tids.length} added)
                </span>
              </label>
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".txt,.csv"
                  onChange={handleFileImport}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-950 border border-zinc-800 hover:border-emerald-500/40 hover:text-emerald-400 text-zinc-400 text-[11px] font-bold uppercase tracking-wider transition-all micro-interaction"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1M12 12V4m0 0L8 8m4-4l4 4" />
                  </svg>
                  Import File
                </button>
                {tids.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setTids([])}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-500/10 border border-rose-500/20 hover:bg-rose-500/20 text-rose-400 text-[11px] font-bold uppercase tracking-wider transition-all micro-interaction"
                  >
                    Clear All
                  </button>
                )}
              </div>
            </div>

            <div
              className="w-full min-h-[110px] max-h-60 overflow-y-auto custom-scrollbar px-3 py-3 bg-zinc-950 border border-zinc-800 rounded-2xl focus-within:border-emerald-500/50 focus-within:ring-2 focus-within:ring-emerald-500/10 transition-all flex flex-wrap gap-2 items-start"
            >
              {tids.map((tid) => (
                <span
                  key={tid}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 text-xs font-bold font-mono transition-all animate-spring-in"
                >
                  {tid}
                  <button
                    type="button"
                    onClick={() => removeTid(tid)}
                    className="hover:text-rose-400 transition-colors"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              ))}
              <input
                type="text"
                value={tidInput}
                onChange={(e) => setTidInput(e.target.value)}
                onKeyDown={handleTidKeyDown}
                onPaste={handleTidPaste}
                onBlur={() => {
                  if (tidInput.trim()) {
                    addTidValue(tidInput);
                    setTidInput('');
                  }
                }}
                placeholder={tids.length ? 'Add more...' : 'Paste or type TIDs (comma / space / enter separated)'}
                className="flex-1 min-w-[180px] bg-transparent outline-none text-sm font-mono text-zinc-100 placeholder:text-zinc-600 py-1"
              />
            </div>
            <p className="text-[10px] font-medium text-zinc-500">
              Press <span className="text-zinc-300 font-bold">Enter</span>, <span className="text-zinc-300 font-bold">Space</span>, or <span className="text-zinc-300 font-bold">Comma</span> to add a TID. Backspace removes the last one. You can also paste comma-separated lists or import a <span className="text-zinc-300 font-bold">.txt / .csv</span> file.
            </p>
          </div>

          {/* ===== PARALLEL MODE + FIRE BUTTON ===== */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-4 border-t border-zinc-800/60">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setParallelMode((v) => !v)}
                className={`relative w-12 h-6 rounded-full transition-colors duration-300 ${
                  parallelMode ? 'bg-emerald-500' : 'bg-zinc-700'
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-zinc-950 shadow transition-transform duration-300 ${
                    parallelMode ? 'translate-x-6' : 'translate-x-0'
                  }`}
                />
              </button>
              <div>
                <p className="text-sm font-bold text-zinc-200">Parallel Firing</p>
                <p className="text-[10px] font-medium text-zinc-500">
                  {parallelMode
                    ? 'All postbacks fire simultaneously (faster)'
                    : 'Postbacks fire sequentially (safer)'}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={firePostback}
              disabled={isFiring}
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl bg-orange-500 hover:bg-orange-400 text-zinc-950 font-black text-sm uppercase tracking-wider shadow-lg shadow-orange-500/20 transition-all micro-interaction active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isFiring ? (
                <>
                  <span className="w-4 h-4 border-2 border-zinc-950 border-t-transparent rounded-full animate-spin" />
                  Firing...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Fire Postback
                </>
              )}
            </button>
          </div>
        </div>

        {/* ========== RESULTS TABLE ========== */}
        {results.length > 0 && (
          <div className="bg-zinc-900/90 border border-zinc-800 rounded-3xl overflow-hidden shadow-lg animate-spring-in">
            <div className="px-6 py-5 border-b border-zinc-800/60 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-bold text-zinc-100">Postback Results</h3>
                <p className="text-xs text-zinc-400 mt-0.5">
                  {results.length} request{results.length !== 1 ? 's' : ''} processed · Page {safePage} of {totalPages}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={retryAllFailed}
                  disabled={isFiring || errorCount === 0}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20 hover:bg-amber-500/20 text-amber-400 text-[11px] font-bold uppercase tracking-wider transition-all micro-interaction disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Retry All Failed ({errorCount})
                </button>
                <button
                  type="button"
                  onClick={exportFailedTids}
                  disabled={errorCount === 0}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-rose-500/10 border border-rose-500/20 hover:bg-rose-500/20 text-rose-400 text-[11px] font-bold uppercase tracking-wider transition-all micro-interaction disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1M12 12V4m0 0l4 4m-4-4L8 8" />
                  </svg>
                  Export Failed CSV
                </button>
              </div>
            </div>

            <div className="overflow-x-auto overflow-y-auto custom-scrollbar max-h-[520px]">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-zinc-950 border-b border-zinc-800">
                    <th className="text-left px-6 py-3.5 text-[10px] font-black uppercase tracking-widest text-zinc-500">
                      TID
                    </th>
                    <th className="text-left px-6 py-3.5 text-[10px] font-black uppercase tracking-widest text-zinc-500">
                      Event
                    </th>
                    <th className="text-left px-6 py-3.5 text-[10px] font-black uppercase tracking-widest text-zinc-500">
                      Status
                    </th>
                    <th className="text-left px-6 py-3.5 text-[10px] font-black uppercase tracking-widest text-zinc-500">
                      Response
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800/60">
                  {paginatedResults.map((row, i) => {
                    const idx = (safePage - 1) * rowsPerPage + i;
                    const eventName = availableEvents.find((e) => e.id === row.eventId)?.name || row.eventId;
                    return (
                      <tr key={idx} className="hover:bg-zinc-950/40 transition-colors">
                        <td className="px-6 py-3.5 font-mono text-xs text-zinc-200">{row.tid}</td>
                        <td className="px-6 py-3.5 text-xs font-semibold text-zinc-300">{eventName}</td>
                        <td className="px-6 py-3.5">
                          {row.status === 'pending' && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-500/10 text-amber-400 border border-amber-500/20 text-[10px] font-bold uppercase">
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                              Pending
                            </span>
                          )}
                          {row.status === 'success' && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-[10px] font-bold uppercase">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                              </svg>
                              Success
                            </span>
                          )}
                          {row.status === 'error' && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-rose-500/10 text-rose-400 border border-rose-500/20 text-[10px] font-bold uppercase">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                              Failed
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-3.5 text-xs text-zinc-400 max-w-md truncate" title={row.response || ''}>
                          {row.response || '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* ===== PAGINATION ===== */}
            <div className="px-6 py-4 border-t border-zinc-800/60 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">
                Showing {(safePage - 1) * rowsPerPage + 1}–
                {Math.min(safePage * rowsPerPage, results.length)} of {results.length}
              </p>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  className="px-3 py-1.5 rounded-lg bg-zinc-950 border border-zinc-800 text-zinc-300 hover:text-emerald-400 hover:border-emerald-500/40 text-[11px] font-bold uppercase transition-all micro-interaction disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Prev
                </button>
                {Array.from({ length: totalPages }).map((_, i) => {
                  const page = i + 1;
                  // Show first, last, current, and neighbors; hide others with ellipsis
                  const show =
                    page === 1 ||
                    page === totalPages ||
                    Math.abs(page - safePage) <= 1;
                  if (!show) {
                    if (
                      (page === 2 && safePage > 3) ||
                      (page === totalPages - 1 && safePage < totalPages - 2)
                    ) {
                      return (
                        <span key={page} className="px-1 text-zinc-600 text-xs font-bold">
                          …
                        </span>
                      );
                    }
                    return null;
                  }
                  return (
                    <button
                      key={page}
                      type="button"
                      onClick={() => setCurrentPage(page)}
                      className={`min-w-[32px] px-2.5 py-1.5 rounded-lg text-[11px] font-bold transition-all micro-interaction ${
                        page === safePage
                          ? 'bg-emerald-500 text-zinc-950 shadow-lg shadow-emerald-500/20'
                          : 'bg-zinc-950 border border-zinc-800 text-zinc-300 hover:text-emerald-400 hover:border-emerald-500/40'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  className="px-3 py-1.5 rounded-lg bg-zinc-950 border border-zinc-800 text-zinc-300 hover:text-emerald-400 hover:border-emerald-500/40 text-[11px] font-bold uppercase transition-all micro-interaction disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default AdminFirePostbackPage;